/**
 * The Active companies chart's figures (SF-5124): the series months turned into one stacked
 * point per month, and each band's share of the month's total.
 */
import type { PlatformSeriesMonth } from '../../../components/usage/usageClient';
import { formatBucketLabel } from '../../../utils/date';

/** The bands, bottom first. */
export const ACTIVE_BANDS = [
  { key: 'enterprise', label: 'Enterprise' },
  { key: 'self_serve_paid', label: 'Self-serve paid' },
  { key: 'free', label: 'Free' },
] as const;

export type BandKey = (typeof ACTIVE_BANDS)[number]['key'];

export interface ActivePoint extends Record<BandKey, number> {
  label: string;
  total: number;
}

/** One point per month that has the active figures. */
export function activePoints(months: PlatformSeriesMonth[] | undefined): ActivePoint[] {
  return (months ?? []).flatMap((m) =>
    m.active
      ? [
          {
            label: formatBucketLabel(m.period, 'monthly'),
            total: m.active.total,
            enterprise: m.active.enterprise,
            self_serve_paid: m.active.self_serve_paid,
            free: m.active.free,
          },
        ]
      : [],
  );
}

/** Whether the API sent the active figures for any month. */
export function hasActiveSeries(months: PlatformSeriesMonth[] | undefined): boolean {
  return (months ?? []).some((m) => m.active !== undefined);
}

/** A band's share of the month's total, or null when the total is 0. */
export function bandShare(count: number, total: number): number | null {
  return total > 0 ? (count / total) * 100 : null;
}
